import { get_testing_type, get_testing_list, get_testing_schedule_list } from './restapi';

const parseDate = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(/\./g, '-').replace(/\//g, '-').trim();
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

/**
 * 정기시험 유형과 '이번 회차' 시험 1건을 찾는다.
 * 시험일정 기준으로
 *   1) 진행 중(시작일 <= 오늘 <= 종료일) 시험을 최우선 선택
 *   2) 없으면 오늘 이후(미실시) 중 가장 임박한 시험
 *   3) 없으면 오늘 이전(실시됨) 중 가장 최근 시험
 */
// 접수기간 판정은 시각까지 봐야 하므로 날짜만 자르는 parseDate 와 따로 둔다.
// '0000-00-00 00:00:00'(미설정 기본값)은 유효하지 않은 값으로 걸러진다.
const parseDateTime = (value) => {
  if (!value) return null;
  const normalized = String(value).replace(/\./g, '-').replace(/\//g, '-').trim().replace(' ', 'T');
  if (normalized.startsWith('0000')) return null;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isWithin = (now, start, stop) => {
  const startDate = parseDateTime(start);
  const stopDate = parseDateTime(stop);
  if (!startDate || !stopDate) return false;
  return startDate <= now && now <= stopDate;
};

/**
 * 지금 접수를 받고 있는 회차인가?
 * 레거시 PHP(php/klata/html/testing_accept/index.html 등)와 같은 기준으로,
 * 일정 중 하나라도 온라인 접수기간(onlineStartDate~onlineStopDate) 또는
 * 방문 접수기간(offlineStartDate~offlineStopDate) 안에 현재 시각이 들어가면 접수중이다.
 */
export const isAcceptingNow = (round) => {
  const schedules = round?.schedules || [];
  if (!schedules.length) return false;

  const now = new Date();
  return schedules.some((schedule) =>
    isWithin(now, schedule?.onlineStartDate, schedule?.onlineStopDate) ||
    isWithin(now, schedule?.offlineStartDate, schedule?.offlineStopDate)
  );
};

const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  result.setHours(0, 0, 0, 0);
  return result;
};

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/**
 * 지정한 시험유형의 회차 목록을 만든다.
 * /testing_schedule/list 는 type 만으로도 조회되고 응답에 testingObj(회차정보)가 실려 오므로,
 * 회차마다 일정을 따로 조회하지 않고 한 번의 호출로 끝낸다.
 *
 * months 를 주면 '오늘 ±months개월' 과 겹치는 일정을 가진 회차만 남긴다(null이면 전체).
 *
 * 반환: [{ testing_id, title, type, typeName, schedules: [{...schedule, startDate, stopDate}] }]
 *       (회차는 첫 일정 날짜 내림차순 = 최신 먼저, 각 회차의 일정은 오름차순)
 */
export const getTestingRounds = async (typeCode, months = null) => {
  if (typeCode === undefined || typeCode === null || typeCode === '') return [];

  const response = await get_testing_schedule_list({
    type: typeCode,
    page: 1,
    rowsPerPage: 1000
  });

  const schedules = response?.lists || response || [];
  const today = startOfToday();
  const rangeStart = months === null ? null : addMonths(today, -months);
  const rangeStop = months === null ? null : addMonths(today, months);

  const roundMap = new Map();

  for (const schedule of schedules) {
    const testing = schedule?.testingObj || {};
    const testingId = testing.testing_id || schedule?.testing_id;
    if (!testingId) continue;

    const startDate = parseDate(schedule?.testStartDate || schedule?.testStopDate);
    const stopDate = parseDate(schedule?.testStopDate || schedule?.testStartDate);
    if (!startDate || !stopDate) continue;

    // 범위를 지정한 경우, 일정 기간이 범위와 조금이라도 겹쳐야 포함
    if (rangeStart && (stopDate < rangeStart || startDate > rangeStop)) continue;

    if (!roundMap.has(testingId)) {
      roundMap.set(testingId, {
        testing_id: testingId,
        title: testing.title || '',
        type: testing.type !== undefined ? testing.type : typeCode,
        typeName: testing.typeName || '',
        schedules: []
      });
    }
    roundMap.get(testingId).schedules.push({ ...schedule, startDate, stopDate });
  }

  const rounds = Array.from(roundMap.values());
  rounds.forEach((round) => round.schedules.sort((a, b) => a.startDate - b.startDate));
  rounds.sort((a, b) => b.schedules[0].startDate - a.schedules[0].startDate);

  return rounds;
};

/**
 * 회차 목록에서 '이번 회차' 1건을 고른다.
 *   1) 진행 중(시작일 <= 오늘 <= 종료일)  2) 임박한 예정  3) 최근 종료
 */
export const pickCurrentRound = (rounds) => {
  if (!rounds || !rounds.length) return null;

  const today = startOfToday();
  let inProgress = null;
  let nearestUpcoming = null;
  let latestPast = null;

  for (const round of rounds) {
    for (const schedule of round.schedules) {
      const { startDate, stopDate } = schedule;
      if (!startDate || !stopDate) continue;

      if (startDate <= today && today <= stopDate) {
        if (!inProgress || stopDate < inProgress.date) inProgress = { round, date: stopDate };
      } else if (stopDate >= today) {
        if (!nearestUpcoming || stopDate < nearestUpcoming.date) nearestUpcoming = { round, date: stopDate };
      } else {
        if (!latestPast || stopDate > latestPast.date) latestPast = { round, date: stopDate };
      }
    }
  }

  return inProgress?.round || nearestUpcoming?.round || latestPast?.round || rounds[0];
};

export const getCurrentRegularTesting = async () => {
  const response = await get_testing_type();
  const types = response || [];

  const regularType = types.find((type) => {
    const name = `${type?.nickname || ''}${type?.name || ''}`;
    return name.includes('정기');
  }) || null;

  if (!regularType?.code) {
    return { types, regularType: null, testing: null, regularTestings: [] };
  }

  const testingResponse = await get_testing_list({
    type: regularType.code,
    page: 1,
    rowsPerPage: 1000,
    sort: 'a_testing_id',
    desc: 'desc'
  });

  const regularTestings = testingResponse?.lists || testingResponse || [];
  if (!regularTestings.length) {
    return { types, regularType, testing: null, regularTestings: [] };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let inProgress = null;
  let nearestUpcoming = null;
  let latestPast = null;
  const candidates = regularTestings.slice(0, 100);

  for (const testing of candidates) {
    try {
      const scheduleResponse = await get_testing_schedule_list({
        testing_id: testing.testing_id,
        page: 1,
        rowsPerPage: 1000,
        sort: 'a_testStopDate',
        desc: 'desc'
      });

      const schedules = scheduleResponse?.lists || scheduleResponse || [];
      for (const schedule of schedules) {
        const startDate = parseDate(schedule?.testStartDate || schedule?.startDate || schedule?.testStopDate || schedule?.stopDate);
        const stopDate = parseDate(schedule?.testStopDate || schedule?.stopDate || schedule?.testStartDate || schedule?.startDate);
        if (!startDate || !stopDate) continue;

        const isInProgress = startDate <= today && today <= stopDate;
        if (isInProgress) {
          if (!inProgress || stopDate < inProgress.date) {
            inProgress = { testing, date: stopDate };
          }
          continue;
        }

        if (stopDate >= today) {
          if (!nearestUpcoming || stopDate < nearestUpcoming.date) {
            nearestUpcoming = { testing, date: stopDate };
          }
        } else {
          if (!latestPast || stopDate > latestPast.date) {
            latestPast = { testing, date: stopDate };
          }
        }
      }
    } catch (error) {
      console.error('Error fetching testing schedule:', error);
    }
  }

  const testing = inProgress?.testing || nearestUpcoming?.testing || latestPast?.testing || regularTestings[0];

  // regularTestings: 정기시험 전체 회차 목록(최신순). 운영계획 페이지에서 테스트용으로
  // 지난 회차를 직접 선택할 수 있도록 함께 반환한다.
  return { types, regularType, testing, regularTestings };
};
